import { useRef, useState } from "react";
import {
  INK,
  MUTED,
  MUTED_SOFT,
  SAND,
  LINE,
  LINE_STRONG,
  IMAGE_BG,
  EASE,
} from "@/lib/marketing-theme";
import { motion, useReducedMotion } from "framer-motion";
import posthog from "posthog-js";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  CalendarClock,
  Mail,
  MapPin,
  Paperclip,
  Send,
  X,
  Loader2,
  ArrowUpRight,
} from "lucide-react";
import { WhatsAppIcon } from "@/components/marketing/whatsapp-icon";
import { useLanguage } from "@/contexts/LanguageContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "../lib/supabase";

// Mirrors the server-side allowlist in supabase/functions/send-contact-email/attachment-validation.ts.
// The extension is checked too — the server rejects a type/extension mismatch,
// so catching it here just saves the user a round trip.
const ALLOWED_ATTACHMENT_EXTENSIONS_BY_TYPE: Record<string, string[]> = {
  "application/pdf": ["pdf"],
  "image/jpeg": ["jpg", "jpeg"],
  "image/jpg": ["jpg", "jpeg"],
  "image/png": ["png"],
  "image/webp": ["webp"],
};
const ALLOWED_ATTACHMENT_TYPES = Object.keys(
  ALLOWED_ATTACHMENT_EXTENSIONS_BY_TYPE
);
const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024; // 5MB per file
const MAX_ATTACHMENTS_COUNT = 5;
const MAX_ATTACHMENTS_TOTAL_BYTES = 15 * 1024 * 1024; // 15MB combined

function fileExtension(filename: string): string {
  const dot = filename.lastIndexOf(".");
  return dot === -1 ? "" : filename.slice(dot + 1).toLowerCase();
}

function isAllowedAttachment(file: File): boolean {
  const allowedExtensions = ALLOWED_ATTACHMENT_EXTENSIONS_BY_TYPE[file.type];
  return (
    !!allowedExtensions && allowedExtensions.includes(fileExtension(file.name))
  );
}

function formatFileSize(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Strip the "data:<mime>;base64," prefix — Resend only wants the raw payload.
      resolve(result.slice(result.indexOf(",") + 1));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

type ContactWay = {
  icon: typeof Mail;
  nameKey: string;
  noteKey: string;
  /** A literal value (address, number) or a key when the value is prose. */
  value?: string;
  valueKey?: string;
  href: string;
  external: boolean;
};

/**
 * Headline reveal: each word rides up out of its own clipping box so the line
 * assembles rather than fading in as one block.
 */
function WordReveal({
  text,
  className,
  style,
}: {
  text: string;
  className?: string;
  style?: React.CSSProperties;
}) {
  const reduce = useReducedMotion();
  const words = text.split(" ");
  return (
    <h1 className={className} style={style}>
      {words.map((word, i) => (
        <span key={`${word}-${i}`}>
          <span className="inline-block overflow-hidden align-bottom">
            <motion.span
              className="inline-block"
              initial={reduce ? undefined : { y: "108%" }}
              animate={reduce ? undefined : { y: "0%" }}
              transition={{ duration: 0.75, ease: EASE, delay: i * 0.05 }}
            >
              {word}
            </motion.span>
          </span>
          {i < words.length - 1 ? " " : null}
        </span>
      ))}
    </h1>
  );
}

/** The three ways to reach us, in the draft's order. */
const CONTACT_WAYS: ContactWay[] = [
  {
    icon: WhatsAppIcon,
    nameKey: "contact.whatsapp",
    noteKey: "contact.whatsappNote",
    value: "+49 89 54193354",
    href: "https://wa.me/498954193354",
    external: true,
  },
  {
    icon: Mail,
    nameKey: "contact.emailWay",
    noteKey: "contact.emailNote",
    value: "kontakt@coliviq.de",
    href: "mailto:kontakt@coliviq.de",
    external: false,
  },
  {
    icon: CalendarClock,
    nameKey: "contact.demo",
    noteKey: "contact.demoNote",
    valueKey: "contact.demoValue",
    href: "mailto:kontakt@coliviq.de?subject=Demo",
    external: false,
  },
];

const MAP_EMBED_SRC =
  "https://maps.google.com/maps?q=" +
  encodeURIComponent("Elvirastraße 10, 80636 München") +
  "&z=16&output=embed";

const MAP_LINK =
  "https://www.google.com/maps/search/?api=1&query=" +
  encodeURIComponent("Elvirastraße 10, 80636 München");

/** "Worum geht's?" — the selector drives the message placeholder. */
const CONTACT_ROLES = [
  { key: "contact.role.room", placeholder: "contact.role.room.ph" },
  { key: "contact.role.landlord", placeholder: "contact.role.landlord.ph" },
  { key: "contact.role.project", placeholder: "contact.role.project.ph" },
] as const;

const Contact = () => {
  const { t, language } = useLanguage();
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    city: "",
    message: "",
  });
  /** "Worum geht's?" — index into CONTACT_ROLES. */
  const [role, setRole] = useState(0);
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Cinematic hero parallax (matches the Concept hero)

  // Fallback translations if Storyblok doesn't have them
  const fallbackTranslations: Record<string, Record<string, string>> = {
    en: {
      "contact.title": "Get in Touch",
      "contact.subtitle": "Have questions? We'll help.",
      "contact.emailLabel": "Email",
      "contact.phoneLabel": "Phone",
      "contact.officeLabel": "Office",
      "contact.name": "Name",
      "contact.namePlaceholder": "First and last name",
      "contact.email": "Email",
      "contact.emailPlaceholder": "your.email@example.com",
      "contact.message": "Message",
      "contact.messagePlaceholder": "Tell us how we can help...",
      "contact.send": "Send Message",
      "contact.sending": "Sending...",
      "contact.success": "Message sent!",
      "contact.successDescription": "We'll get back to you soon.",
      "contact.error": "Error",
      "contact.errorMessage": "Failed to send message. Please try again.",
      "contact.attach": "Attach file",
      "contact.heading": "Get in touch.",
      "contact.lead":
        "Room, management or project — we usually reply the same working day.",
      "contact.whatsapp": "WhatsApp",
      "contact.whatsappNote": "Fastest — Mon–Fri, 9–18",
      "contact.emailWay": "Email",
      "contact.emailNote": "For anything with an attachment",
      "contact.demo": "Demo",
      "contact.demoValue": "Book a 15-minute call",
      "contact.demoNote": "For landlords and project partners",
      "contact.roleLabel": "What is it about?",
      "contact.role.room": "I'm looking for a room",
      "contact.role.room.ph": "Which city, from when, what budget?",
      "contact.role.landlord": "I let a shared flat",
      "contact.role.landlord.ph":
        "How many rooms, where, and what should coliviq take on?",
      "contact.role.project": "I have a project",
      "contact.role.project.ph": "Property type, area and timeline.",
      "contact.phone": "Phone",
      "contact.phoneHint": "optional",
      "contact.city": "City",
      "contact.cityHint": "e.g. Munich",
      "contact.visit": "Or come by.",
      "contact.visitNote": "By appointment.",
      "contact.mapTitle": "Map: Elvirastraße 10, 80636 Munich",
      "contact.mapOpen": "Open in Google Maps",
      "contact.removeAttachment": "Remove attachment",
      "contact.attachmentInvalidType":
        "isn't a supported file type (PDF, JPEG, PNG, WebP).",
      "contact.attachmentTooLarge": "is larger than 5MB.",
      "contact.attachmentTooMany": `You can attach up to ${MAX_ATTACHMENTS_COUNT} files.`,
      "contact.attachmentTotalTooLarge": "Total attachments size exceeds 15MB.",
    },
    de: {
      "contact.title": "Kontaktiere uns",
      "contact.subtitle": "Hast du Fragen? Wir helfen gerne.",
      "contact.emailLabel": "E-Mail",
      "contact.phoneLabel": "Telefon",
      "contact.officeLabel": "Büro",
      "contact.name": "Name",
      "contact.namePlaceholder": "Vor- und Nachname",
      "contact.email": "E-Mail",
      "contact.emailPlaceholder": "ihre.email@beispiel.de",
      "contact.message": "Nachricht",
      "contact.messagePlaceholder":
        "Teilen Sie uns mit, wie wir helfen können...",
      "contact.send": "Nachricht senden",
      "contact.sending": "Wird gesendet...",
      "contact.success": "Nachricht gesendet!",
      "contact.successDescription": "Wir melden uns bald bei Ihnen.",
      "contact.error": "Fehler",
      "contact.errorMessage":
        "Nachricht konnte nicht gesendet werden. Bitte versuchen Sie es erneut.",
      "contact.attach": "Datei anhängen",
      "contact.heading": "Kontakt aufnehmen.",
      "contact.lead":
        "Ob Zimmer, Verwaltung oder Projekt — wir antworten in der Regel am selben Werktag.",
      "contact.whatsapp": "WhatsApp",
      "contact.whatsappNote": "Am schnellsten — Mo–Fr, 9–18 Uhr",
      "contact.emailWay": "E-Mail",
      "contact.emailNote": "Für alles mit Anhang",
      "contact.demo": "Demo",
      "contact.demoValue": "15-Minuten-Call buchen",
      "contact.demoNote": "Für WG-Vermieter und Projektpartner",
      "contact.roleLabel": "Worum geht’s?",
      "contact.role.room": "Ich suche ein WG-Zimmer",
      "contact.role.room.ph": "Welche Stadt, ab wann, welches Budget?",
      "contact.role.landlord": "Ich vermiete eine WG",
      "contact.role.landlord.ph":
        "Wie viele Zimmer, wo, und was soll coliviq übernehmen?",
      "contact.role.project": "Ich habe ein Projekt",
      "contact.role.project.ph": "Objektart, Fläche und Zeitplan.",
      "contact.phone": "Telefon",
      "contact.phoneHint": "optional",
      "contact.city": "Stadt",
      "contact.cityHint": "z. B. München",
      "contact.visit": "Oder komm vorbei.",
      "contact.visitNote": "Termine nach Vereinbarung.",
      "contact.mapTitle": "Karte: Elvirastraße 10, 80636 München",
      "contact.mapOpen": "In Google Maps öffnen",
      "contact.removeAttachment": "Anhang entfernen",
      "contact.attachmentInvalidType":
        "hat ein nicht unterstütztes Dateiformat (PDF, JPEG, PNG, WebP).",
      "contact.attachmentTooLarge": "ist größer als 5MB.",
      "contact.attachmentTooMany": `Sie können bis zu ${MAX_ATTACHMENTS_COUNT} Dateien anhängen.`,
      "contact.attachmentTotalTooLarge":
        "Die Gesamtgröße der Anhänge überschreitet 15MB.",
    },
  };

  // Helper function to get translation with fallback
  const getTranslation = (key: string): string => {
    const translation = t(key);
    const trimmedTranslation = translation?.trim();
    // If translation is empty, echoes the key back, is truncated, or has a
    // malformed apostrophe, use fallback
    if (
      !trimmedTranslation ||
      trimmedTranslation === key ||
      trimmedTranslation.includes("We\\") ||
      (key === "contact.subtitle" && trimmedTranslation.endsWith("We"))
    ) {
      return (
        fallbackTranslations[language]?.[key] ||
        fallbackTranslations["en"][key] ||
        ""
      );
    }
    return translation;
  };

  const handleFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFiles = Array.from(e.target.files ?? []);
    e.target.value = ""; // allow re-selecting the same file after removal

    if (newFiles.length === 0) return;

    const rejections: string[] = [];
    const accepted: File[] = [];

    for (const file of newFiles) {
      if (!isAllowedAttachment(file)) {
        rejections.push(
          `${file.name} ${getTranslation("contact.attachmentInvalidType")}`
        );
        continue;
      }
      if (file.size > MAX_ATTACHMENT_BYTES) {
        rejections.push(
          `${file.name} ${getTranslation("contact.attachmentTooLarge")}`
        );
        continue;
      }
      accepted.push(file);
    }

    let combined = [...attachedFiles, ...accepted];

    if (combined.length > MAX_ATTACHMENTS_COUNT) {
      rejections.push(getTranslation("contact.attachmentTooMany"));
      combined = attachedFiles; // reject the whole new batch, keep what was already attached
    } else {
      const totalBytes = combined.reduce((sum, f) => sum + f.size, 0);
      if (totalBytes > MAX_ATTACHMENTS_TOTAL_BYTES) {
        rejections.push(getTranslation("contact.attachmentTotalTooLarge"));
        combined = attachedFiles;
      }
    }

    if (rejections.length > 0) {
      toast({
        title: getTranslation("contact.error"),
        description: rejections.join(" "),
        variant: "destructive",
      });
    }

    setAttachedFiles(combined);
  };

  const removeAttachment = (index: number) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      console.log("Sending contact form enquiry...");

      const attachments = await Promise.all(
        attachedFiles.map(async (file) => ({
          filename: file.name,
          contentType: file.type,
          content: await fileToBase64(file),
        }))
      );

      const { data, error } = await supabase.functions.invoke(
        "send-contact-email",
        {
          body: {
            name: formData.name,
            email: formData.email,
            // The draft adds a subject, phone and city that the
            // send-contact-email payload has no fields for. They are prefixed
            // onto the message rather than dropped, so the team still sees
            // them without the edge function having to change.
            message: [
              `${getTranslation("contact.roleLabel")}: ${getTranslation(
                CONTACT_ROLES[role].key
              )}`,
              formData.phone &&
                `${getTranslation("contact.phone")}: ${formData.phone}`,
              formData.city &&
                `${getTranslation("contact.city")}: ${formData.city}`,
              "",
              formData.message,
            ]
              .filter(Boolean)
              .join("\n"),
            language: language, // Pass user's selected language
            attachments,
          },
        }
      );

      if (error) {
        throw error;
      }

      posthog.capture("contact message sent");

      toast({
        title: getTranslation("contact.success"),
        description: getTranslation("contact.successDescription"),
      });

      // Reset form
      setFormData({
        name: "",
        email: "",
        phone: "",
        city: "",
        message: "",
      });
      setAttachedFiles([]);
    } catch (error: any) {
      console.error("Error sending contact form:", error);
      toast({
        title: getTranslation("contact.error"),
        description: error.message || getTranslation("contact.errorMessage"),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <section className="mx-auto w-full max-w-[1180px] px-6 pt-[92px] pb-[80px] sm:px-8 lg:pt-[112px]">
        <div className="grid grid-cols-1 gap-[48px] lg:grid-cols-[minmax(0,0.82fr)_minmax(0,1fr)] lg:gap-[64px]">
          {/* Left column — heading, lead and the three ways to reach us */}
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, ease: EASE }}
          >
            <WordReveal
              text={getTranslation("contact.heading")}
              className="font-display text-[38px] font-semibold leading-[1.06] tracking-[-0.025em] sm:text-[46px] lg:text-[52px]"
              style={{ color: INK }}
            />
            <p
              className="mt-4 max-w-[30ch] text-[16px] leading-[1.6] sm:text-[17px]"
              style={{ color: MUTED }}
            >
              {getTranslation("contact.lead")}
            </p>

            <div
              className="mt-10 border-t"
              style={{ borderColor: LINE_STRONG }}
            >
              {CONTACT_WAYS.map((way, i) => {
                const Icon = way.icon;
                return (
                  <motion.a
                    key={way.nameKey}
                    href={way.href}
                    target={way.external ? "_blank" : undefined}
                    rel={way.external ? "noreferrer noopener" : undefined}
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{
                      duration: 0.5,
                      ease: EASE,
                      delay: 0.08 + i * 0.07,
                    }}
                    className="group flex items-start gap-4 border-b py-5 transition-colors"
                    style={{ borderColor: LINE_STRONG }}
                  >
                    <span
                      className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-transform duration-300 group-hover:scale-110"
                      style={{ background: SAND, color: INK }}
                    >
                      <Icon className="h-4 w-4" strokeWidth={1.6} />
                    </span>
                    <div className="min-w-0">
                      <span
                        className="block text-[11px] font-semibold uppercase tracking-[0.16em]"
                        style={{ color: MUTED_SOFT }}
                      >
                        {getTranslation(way.nameKey)}
                      </span>
                      <span
                        className="mt-1.5 block text-[17px] font-medium transition-transform duration-300 group-hover:translate-x-0.5"
                        style={{ color: INK }}
                      >
                        {way.value ?? getTranslation(way.valueKey!)}
                      </span>
                      <span
                        className="mt-0.5 block text-[13.5px]"
                        style={{ color: MUTED }}
                      >
                        {getTranslation(way.noteKey)}
                      </span>
                    </div>
                    <ArrowUpRight
                      aria-hidden="true"
                      className="ml-auto mt-1 h-4 w-4 shrink-0 opacity-0 transition-all duration-300 group-hover:translate-x-0.5 group-hover:opacity-100"
                      strokeWidth={1.8}
                      style={{ color: MUTED_SOFT }}
                    />
                  </motion.a>
                );
              })}
            </div>
          </motion.div>

          {/* Right column — the form */}
          <motion.div
            initial={{ opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.65, ease: EASE, delay: 0.1 }}
            className="rounded-[18px] border p-6 sm:p-9"
            style={{ background: SAND, borderColor: LINE }}
          >
            <form onSubmit={handleSubmit}>
              {/* Worum geht's? — drives the message placeholder */}
              <fieldset>
                <legend
                  className="text-[11px] font-semibold uppercase tracking-[0.16em]"
                  style={{ color: MUTED_SOFT }}
                >
                  {getTranslation("contact.roleLabel")}
                </legend>
                <div className="mt-3 flex flex-wrap gap-2">
                  {CONTACT_ROLES.map((entry, i) => {
                    const active = role === i;
                    return (
                      <button
                        key={entry.key}
                        type="button"
                        aria-pressed={active}
                        onClick={() => setRole(i)}
                        className="relative rounded-full px-4 py-2 text-[13.5px] font-medium transition-colors"
                        style={{
                          color: active ? "#FFFFFF" : INK,
                          border: `1px solid ${active ? "transparent" : LINE_STRONG}`,
                        }}
                      >
                        {active && (
                          <motion.span
                            layoutId="contact-role"
                            transition={{
                              type: "spring",
                              stiffness: 420,
                              damping: 36,
                            }}
                            className="absolute inset-0 rounded-full"
                            style={{ background: INK }}
                          />
                        )}
                        <span className="relative z-10">
                          {getTranslation(entry.key)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </fieldset>

              <div className="mt-7 grid grid-cols-1 gap-x-5 gap-y-5 sm:grid-cols-2">
                <div>
                  <label
                    htmlFor="contact-name"
                    className="mb-2 block text-[13px] font-medium"
                    style={{ color: INK }}
                  >
                    {getTranslation("contact.name")}{" "}
                    <span aria-hidden="true" style={{ color: MUTED_SOFT }}>
                      *
                    </span>
                  </label>
                  <Input
                    id="contact-name"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    required
                    className="h-12 rounded-[10px] border bg-white text-[15px]"
                    style={{ borderColor: LINE_STRONG, color: INK }}
                    placeholder={getTranslation("contact.namePlaceholder")}
                  />
                </div>

                <div>
                  <label
                    htmlFor="contact-email"
                    className="mb-2 block text-[13px] font-medium"
                    style={{ color: INK }}
                  >
                    {getTranslation("contact.email")}{" "}
                    <span aria-hidden="true" style={{ color: MUTED_SOFT }}>
                      *
                    </span>
                  </label>
                  <Input
                    id="contact-email"
                    type="email"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    required
                    className="h-12 rounded-[10px] border bg-white text-[15px]"
                    style={{ borderColor: LINE_STRONG, color: INK }}
                    placeholder={getTranslation("contact.emailPlaceholder")}
                  />
                </div>

                <div>
                  <label
                    htmlFor="contact-phone"
                    className="mb-2 block text-[13px] font-medium"
                    style={{ color: INK }}
                  >
                    {getTranslation("contact.phone")}
                  </label>
                  <Input
                    id="contact-phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) =>
                      setFormData({ ...formData, phone: e.target.value })
                    }
                    className="h-12 rounded-[10px] border bg-white text-[15px]"
                    style={{ borderColor: LINE_STRONG, color: INK }}
                    placeholder={getTranslation("contact.phoneHint")}
                  />
                </div>

                <div>
                  <label
                    htmlFor="contact-city"
                    className="mb-2 block text-[13px] font-medium"
                    style={{ color: INK }}
                  >
                    {getTranslation("contact.city")}
                  </label>
                  <Input
                    id="contact-city"
                    value={formData.city}
                    onChange={(e) =>
                      setFormData({ ...formData, city: e.target.value })
                    }
                    className="h-12 rounded-[10px] border bg-white text-[15px]"
                    style={{ borderColor: LINE_STRONG, color: INK }}
                    placeholder={getTranslation("contact.cityHint")}
                  />
                </div>
              </div>

              <div className="mt-5">
                <label
                  htmlFor="contact-message"
                  className="mb-2 block text-[13px] font-medium"
                  style={{ color: INK }}
                >
                  {getTranslation("contact.message")}{" "}
                  <span aria-hidden="true" style={{ color: MUTED_SOFT }}>
                    *
                  </span>
                </label>
                <Textarea
                  id="contact-message"
                  value={formData.message}
                  onChange={(e) =>
                    setFormData({ ...formData, message: e.target.value })
                  }
                  required
                  rows={5}
                  placeholder={getTranslation(CONTACT_ROLES[role].placeholder)}
                  className="rounded-[10px] border bg-white text-[15px]"
                  style={{ borderColor: LINE_STRONG, color: INK }}
                />
              </div>

              {/* The draft has no attachment control, but the live form does and
                  the edge function accepts them — kept rather than dropped. */}
              <div className="mt-4">
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept={ALLOWED_ATTACHMENT_TYPES.join(",")}
                  onChange={handleFilesSelected}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="inline-flex items-center gap-2 rounded-full border bg-white px-4 py-2 text-[13px] font-medium transition-colors"
                  style={{ borderColor: LINE_STRONG, color: INK }}
                >
                  <Paperclip className="h-3.5 w-3.5" />
                  {getTranslation("contact.attach")}
                </button>

                {attachedFiles.length > 0 && (
                  <ul className="mt-3 space-y-2">
                    {attachedFiles.map((file, i) => (
                      <li
                        key={`${file.name}-${i}`}
                        className="flex items-center justify-between gap-3 rounded-[10px] border bg-white px-3.5 py-2 text-[13.5px]"
                        style={{ borderColor: LINE_STRONG, color: INK }}
                      >
                        <span className="truncate">
                          {file.name}{" "}
                          <span style={{ color: MUTED_SOFT }}>
                            ({formatFileSize(file.size)})
                          </span>
                        </span>
                        <button
                          type="button"
                          onClick={() => removeAttachment(i)}
                          aria-label={getTranslation(
                            "contact.removeAttachment"
                          )}
                          className="shrink-0 rounded-full p-1 transition-colors hover:bg-black/5"
                          style={{ color: MUTED_SOFT }}
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <motion.button
                type="submit"
                disabled={isSubmitting}
                whileHover={isSubmitting ? undefined : { y: -1 }}
                whileTap={isSubmitting ? undefined : { scale: 0.99 }}
                className="mt-7 inline-flex h-[50px] w-full items-center justify-center gap-2 rounded-full text-[14.5px] font-medium text-white disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto sm:px-9"
                style={{ background: INK }}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {getTranslation("contact.sending")}
                  </>
                ) : (
                  <>
                    {getTranslation("contact.send")}
                    <Send className="h-4 w-4" />
                  </>
                )}
              </motion.button>
            </form>
          </motion.div>
        </div>
      </section>

      {/* Oder komm vorbei. */}
      <section
        className="border-t"
        style={{ background: SAND, borderColor: LINE }}
      >
        <div className="mx-auto grid w-full max-w-[1180px] grid-cols-1 items-center gap-10 px-6 py-[72px] sm:px-8 lg:grid-cols-2 lg:gap-16">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, ease: EASE }}
          >
            <h2
              className="font-display text-[30px] font-semibold tracking-[-0.02em] sm:text-[36px]"
              style={{ color: INK }}
            >
              {getTranslation("contact.visit")}
            </h2>
            <address
              className="mt-5 flex items-start gap-3 not-italic text-[17px] leading-[1.7]"
              style={{ color: INK }}
            >
              <MapPin
                className="mt-1 h-4 w-4 shrink-0"
                strokeWidth={1.6}
                style={{ color: MUTED_SOFT }}
              />
              <span>
                Coliviq GmbH
                <br />
                Elvirastraße 10
                <br />
                80636 München
              </span>
            </address>
            <p className="mt-4 text-[14px]" style={{ color: MUTED }}>
              {getTranslation("contact.visitNote")}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, ease: EASE, delay: 0.1 }}
            className="relative h-[300px] overflow-hidden rounded-[14px] border sm:h-[340px]"
            style={{ background: IMAGE_BG, borderColor: LINE }}
          >
            <iframe
              title={getTranslation("contact.mapTitle")}
              src={MAP_EMBED_SRC}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="absolute inset-0 h-full w-full border-0"
              style={{ filter: "grayscale(1) contrast(1.05)" }}
            />
            <a
              href={MAP_LINK}
              target="_blank"
              rel="noreferrer noopener"
              className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-white/95 px-3.5 py-2 text-[12.5px] font-medium shadow-[0_6px_18px_rgba(9,46,44,0.14)] transition-transform duration-300 hover:-translate-y-0.5"
              style={{ color: INK }}
            >
              <MapPin className="h-3.5 w-3.5" strokeWidth={1.8} />
              {getTranslation("contact.mapOpen")}
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
